import React from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { nFormatter } from "../../../Helpers";
import CurrencySymbol from "../../../Components/common/CurrencySymbol";
import { ExdInfoTip, EXD_TOOLTIP_PROPS } from "./ExdControls";
import {
  getKpiTheme,
  KpiCardShell,
  KpiCardValue,
  KpiHeader,
  KpiMeter,
  KpiSkeleton,
  KpiSubRow,
} from "./kpiMui";

const formatFullAmount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const Sparkline = ({ data = [], tone = "up" }) => {
  const points =
    data.length > 1
      ? data
      : data.length === 1
        ? [data[0], data[0]]
        : [0, 0, 0, 0, 0];
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const width = 52;
  const height = 22;
  const step = width / Math.max(points.length - 1, 1);

  const coords = points.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return { x, y };
  });

  const line = coords
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(" ");
  const area = `${line} L${width},${height} L0,${height} Z`;
  const stroke = tone === "down" ? "#fb7185" : "#4ade80";
  const fill =
    tone === "down" ? "rgba(251, 113, 133, 0.22)" : "rgba(74, 222, 128, 0.22)";

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden
      sx={{
        display: "block",
        flex: "none",
        height: 28,
        ml: "auto",
        pointerEvents: "none",
        width: 64,
      }}
    >
      <path d={area} fill={fill} />
      <path
        d={line}
        fill="none"
        stroke={stroke}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  );
};

const PeriodTrendCard = ({
  title,
  currentValue,
  priorValue,
  currentLabel,
  priorLabel,
  infoTitle,
  loading,
  loginUserCurrency,
  showCurrency = true,
  accent = "performance",
  sparklineData,
  showSparkline = false,
}) => {
  if (loading) return <KpiSkeleton />;

  const theme = getKpiTheme(accent);
  const current = Number(currentValue) || 0;
  const prior = Number(priorValue) || 0;
  const isUp = current > prior;
  const isDown = current < prior;

  const percentChange = (() => {
    if (!prior) return 100;
    const value = ((current - prior) / Math.abs(prior)) * 100;
    if (!Number.isFinite(value)) return 100;
    return Math.min(Math.abs(Number(value.toFixed(1))), 100);
  })();

  const currency = showCurrency ? (
    <>
      {" "}
      <CurrencySymbol currency={loginUserCurrency} withParentheses={false} />
    </>
  ) : null;

  const sparkPoints =
    Array.isArray(sparklineData) && sparklineData.length
      ? sparklineData.map((v) => Number(v) || 0)
      : [prior, current];

  return (
    <KpiCardShell accent={accent}>
      <KpiHeader
        title={title}
        info={<ExdInfoTip title={infoTitle} tone={theme.infoTone} />}
      />

      <Stack direction="row" alignItems="center" spacing={1}>
        <KpiCardValue
          theme={theme}
          tone={isUp ? "good" : isDown ? "bad" : undefined}
        >
          {isDown ? "−" : isUp ? "+" : ""}
          {percentChange}%
        </KpiCardValue>
        {showSparkline ? (
          <Sparkline data={sparkPoints} tone={isDown ? "down" : "up"} />
        ) : null}
      </Stack>

      <KpiMeter value={Math.min(Math.abs(percentChange), 100)} theme={theme} />

      <KpiSubRow sx={{ color: theme.muted }}>
        <Tooltip
          {...EXD_TOOLTIP_PROPS}
          title={`${currentLabel}: ${formatFullAmount(currentValue)}`}
        >
          <Typography
            component="span"
            sx={{
              fontSize: "inherit",
              color: "inherit",
              cursor: "default",
              display: "inline-block",
            }}
          >
            {currentLabel}{" "}
            <Box component="b" sx={{ color: theme.color }}>
              {currentValue ? nFormatter(currentValue, 2) : 0}
              {currency}
            </Box>
          </Typography>
        </Tooltip>
        <Tooltip
          {...EXD_TOOLTIP_PROPS}
          title={`${priorLabel}: ${formatFullAmount(priorValue)}`}
        >
          <Typography
            component="span"
            sx={{
              fontSize: "inherit",
              color: "inherit",
              cursor: "default",
              display: "inline-block",
            }}
          >
            {priorLabel}{" "}
            <Box component="b" sx={{ color: theme.color }}>
              {priorValue ? nFormatter(priorValue, 2) : 0}
              {currency}
            </Box>
          </Typography>
        </Tooltip>
      </KpiSubRow>
    </KpiCardShell>
  );
};

export default PeriodTrendCard;
